// Role options for selection (multi-role support)
import { humanizeRole } from '@/helpers/roles';
import { BASIC_ROLES, Role } from '@/types';
import { CheckboxGroup } from '@nautilusbio/nb-design-system';
import Button from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import Input from '@nautilusbio/nb-design-system/Input';
// Design system components
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import type { ColDef } from 'ag-grid-enterprise';
import clsx from 'clsx';
import { Loader, UserPlus } from 'lucide-react';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import createStyles from '../../../../components/Create/styles.module.css';
import styles from '../styles.module.css';

// Local imports
import ActionsCell from './ActionsCell';
import { TRANSLATION_KEYS } from './constants';
import EditableRolesCell from './EditableRolesCell';
import { useAddUser, useOrgUsers, useRemoveUserHandler } from './hooks';
import type { ManageUsersProps, OrgUser } from './types';

const roleOptions = BASIC_ROLES.map(role => ({
  className: styles.roleLabel,
  children: humanizeRole(role),
  name: role,
  value: role,
}));

/**
 * ManageUsers Component
 *
 * Provides UI for managing users within an organization:
 * - Search and add new users
 * - Assign admin privileges
 * - View current users
 * - Remove users
 */
interface ManageUsersComponentProps extends ManageUsersProps {
  isFullScreen?: boolean;
}

const ManageUsers: FC<ManageUsersComponentProps> = ({ org, onSuccess, isFullScreen = false }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Queries and mutations
  const { data: orgUsers = [], isLoading: isLoadingUsers } = useOrgUsers(org.id);
  const addUserMutation = useAddUser(org.id);
  const handleRemoveUser = useRemoveUserHandler(org.id, org.name, t);

  // Zod schema for validation
  const addUserSchema = z.object({
    email: z.email(t('email_invalid', { ns: 'orgs' })).min(5),
    roles: z.array(z.enum(BASIC_ROLES)).min(1, t('select_at_least_one_role', { ns: 'orgs' })),
  });

  // Form setup
  const form = useForm({
    defaultValues: {
      email: '',
      roles: [] as Role[],
    },
    validationLogic: revalidateLogic(),
    validators: {
      onChange: addUserSchema,
    },
    onSubmit: async ({ value }) => {
      const result = addUserSchema.safeParse(value);

      if (!result.success) {
        // Optionally handle errors here
        return;
      }

      const addUserPromise = addUserMutation.mutateAsync({
        email: value.email,
        roles: value.roles,
      });
      toast.promise(addUserPromise, {
        loading: t(TRANSLATION_KEYS.ADDING),
        success: () => {
          form.reset();
          if (onSuccess) onSuccess();
          return t(TRANSLATION_KEYS.USER_ADDED_SUCCESS, { orgName: org.name });
        },
        error: (error: Error) => {
          return error.message || t(TRANSLATION_KEYS.USER_ADDED_FAILED, { orgName: org.name });
        },
      });
    },
  });

  // Actions cell renderer for user table
  const ActionsCellRenderer = useCallback(
    (props: { data: OrgUser }) => (
      <ActionsCell
        containerRef={containerRef}
        email={props.data.email}
        onRemove={handleRemoveUser}
        roles={props.data.roles}
        userName={props.data.full_name}
      />
    ),
    [handleRemoveUser],
  );

  // Column definitions for users table
  const userColDefs: ColDef<OrgUser>[] = useMemo(
    () => [
      {
        field: 'full_name',
        headerName: t(TRANSLATION_KEYS.NAME),
      },
      {
        field: 'email',
        headerName: t(TRANSLATION_KEYS.EMAIL),
      },
      {
        field: 'roles',
        headerName: t(TRANSLATION_KEYS.ROLE),
        cellRenderer: (params: { data: OrgUser }) => (
          <EditableRolesCell
            email={params.data.email}
            orgId={org.id}
            roles={params.data.roles}
            userName={params.data.full_name}
          />
        ),
        width: 350,
      },
      {
        cellRenderer: ActionsCellRenderer,
        field: 'id',
        filter: false,
        headerName: t(TRANSLATION_KEYS.ACTIONS),
        sortable: false,
        suppressAutoSize: true,
        width: 50,
      },
    ],
    [org?.id, t, ActionsCellRenderer],
  );

  return isFullScreen ? (
    <Stack fullWidth orientation="vertical" ref={containerRef}>
      {/* Filters/Add User Section as first row */}
      <Stack alignItems="start" fullWidth gap={3}>
        <form
          className={clsx(createStyles.form, styles.formContainer)}
          onSubmit={async e => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <Stack gap={2} orientation="vertical">
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => {
                  const result = addUserSchema.shape.email.safeParse(value);

                  return result.success ? undefined : result.error.issues[0].message;
                },
              }}
            >
              {field => (
                <>
                  <Input
                    data-testid="email-input"
                    fullWidth
                    id="email"
                    label={{ children: t(TRANSLATION_KEYS.SEARCH_USER), htmlFor: 'email', id: 'email-label' }}
                    onChange={e => field.handleChange(e.target.value)}
                    placeholder={t('search_user_placeholder', { ns: 'orgs' })}
                    size="md"
                    value={field.state.value}
                  />
                  {field.state.meta.errors?.[0] && (
                    <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                      {field.state.meta.errors[0]?.toString()}
                    </Text>
                  )}
                </>
              )}
            </form.Field>
            <Text as="p" className={getTextClassName('sm')}>
              {t('search_user_description', { ns: 'orgs' })}
            </Text>
          </Stack>

          <form.Field
            name="roles"
            validators={{
              onChange: ({ value }) => {
                const result = addUserSchema.shape.roles.safeParse(value);

                return result.success ? undefined : result.error.issues[0].message;
              },
            }}
          >
            {field => (
              <>
                <CheckboxGroup
                  className={styles.checkboxGroup}
                  data-testid="roles-checkbox-group"
                  id="roles-checkbox-group"
                  label={t('user_permissions', { ns: 'orgs' })}
                  onChange={value => field.handleChange(value as Role[])}
                  options={roleOptions}
                  size="sm"
                  value={field.state.value}
                />
                {(() => {
                  const err = field.state.meta.errorMap?.onChange;
                  if (!err) return null;
                  if (Array.isArray(err) && err.length > 0) {
                    const first = err[0];
                    if (typeof first === 'object' && first !== null && 'message' in first) {
                      return (
                        <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                          {first.message}
                        </Text>
                      );
                    }
                    if (typeof first === 'string') {
                      return (
                        <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                          {first}
                        </Text>
                      );
                    }
                  }
                  if (typeof err === 'string') {
                    return (
                      <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                        {err}
                      </Text>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </form.Field>

          <form.Subscribe selector={state => [state.isSubmitting, state.isValid, state.isTouched]}>
            {([isSubmitting, isValid, isTouched]) => (
              <Button
                className={clsx(isSubmitting && createStyles.noPointer)}
                data-testid="add-user-btn"
                isDisabled={!isValid || !isTouched}
                size="md"
                type="submit"
                visual={isSubmitting ? 'success' : 'primary'}
              >
                {isSubmitting ? (
                  <>
                    <Loader className={createStyles.spinner} /> {t('adding', { ns: 'general' })}
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    &nbsp;{t('add_user', { ns: 'general' })}
                  </>
                )}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </Stack>

      {/* Current Users Section */}
      <Stack className={styles.manageUsersContainer} fullWidth gap={3} justifyContent="end" orientation="vertical">
        <Text as="h2" className={clsx(getTextClassName('lg'), styles.usersTitle)}>
          {t('current_users', { ns: 'general' })}
        </Text>

        {isLoadingUsers ? (
          <Stack alignItems="center" justifyContent="center">
            <Loader className={createStyles.spinner} />
          </Stack>
        ) : orgUsers.length > 0 ? (
          <Table
            columnDefs={userColDefs}
            container={{ className: styles.usersTable }}
            domLayout="autoHeight"
            enableResponsiveResize={true}
            resizeDebounceMs={150}
            rowData={orgUsers}
            tableId={`manage-users-${org.id}`}
          />
        ) : (
          <Stack alignItems="center" className={styles.emptyState} gap={3} justifyContent="center">
            <Text as="p" className={getTextClassName('sm')}>
              {t('no_users_in_org', { ns: 'orgs' })}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  ) : (
    <Stack className={styles.mainContainer} fullWidth orientation="horizontal" ref={containerRef}>
      {/* Add User Section */}
      <Stack alignItems="start" gap={3}>
        <form
          className={clsx(createStyles.form, styles.formContainer)}
          onSubmit={async e => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <Stack gap={2} orientation="vertical">
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => {
                  const result = addUserSchema.shape.email.safeParse(value);

                  return result.success ? undefined : result.error.issues[0].message;
                },
              }}
            >
              {field => (
                <>
                  <Input
                    data-testid="email-input"
                    fullWidth
                    id="email"
                    label={{ children: t(TRANSLATION_KEYS.SEARCH_USER), htmlFor: 'email', id: 'email-label' }}
                    onChange={e => field.handleChange(e.target.value)}
                    placeholder={t('search_user_placeholder', { ns: 'orgs' })}
                    size="md"
                    value={field.state.value}
                  />
                  {field.state.meta.errors?.[0] && (
                    <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                      {field.state.meta.errors[0]?.toString()}
                    </Text>
                  )}
                </>
              )}
            </form.Field>
            <Text as="p" className={getTextClassName('sm')}>
              {t('search_user_description', { ns: 'orgs' })}
            </Text>
          </Stack>

          <form.Field
            name="roles"
            validators={{
              onChange: ({ value }) => {
                const result = addUserSchema.shape.roles.safeParse(value);

                return result.success ? undefined : result.error.issues[0].message;
              },
            }}
          >
            {field => (
              <>
                <CheckboxGroup
                  className={styles.checkboxGroup}
                  classNames={{
                    label: styles.roleLabel,
                  }}
                  data-testid="roles-checkbox-group"
                  id="roles-checkbox-group"
                  label={t('user_permissions', { ns: 'orgs' })}
                  onChange={value => field.handleChange(value as Role[])}
                  options={roleOptions}
                  size="sm"
                  value={field.state.value}
                />
                {(() => {
                  const err = field.state.meta.errorMap?.onChange;
                  if (!err) return null;
                  if (Array.isArray(err) && err.length > 0) {
                    const first = err[0];
                    if (typeof first === 'object' && first !== null && 'message' in first) {
                      return (
                        <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                          {first.message}
                        </Text>
                      );
                    }
                    if (typeof first === 'string') {
                      return (
                        <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                          {first}
                        </Text>
                      );
                    }
                  }
                  if (typeof err === 'string') {
                    return (
                      <Text as="p" className={clsx(getTextClassName('sm'), styles.errorText)}>
                        {err}
                      </Text>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </form.Field>

          <form.Subscribe selector={state => [state.isSubmitting, state.isValid, state.isTouched]}>
            {([isSubmitting, isValid, isTouched]) => (
              <Button
                className={clsx(isSubmitting && createStyles.noPointer)}
                data-testid="add-user-btn"
                isDisabled={!isValid || !isTouched}
                size="md"
                type="submit"
                visual={isSubmitting ? 'success' : 'primary'}
              >
                {isSubmitting ? (
                  <>
                    <Loader className={createStyles.spinner} /> {t('adding', { ns: 'general' })}
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    &nbsp;{t('add_user', { ns: 'general' })}
                  </>
                )}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </Stack>

      {/* Current Users Section */}
      <Stack className={styles.manageUsersContainer} fullWidth gap={3} justifyContent="end" orientation="vertical">
        <Text as="h2" className={clsx(getTextClassName('lg'), styles.usersTitle)}>
          {t('current_users', { ns: 'general' })}
        </Text>

        {isLoadingUsers ? (
          <Stack alignItems="center" justifyContent="center">
            <Loader className={createStyles.spinner} />
          </Stack>
        ) : orgUsers.length > 0 ? (
          <Table
            columnDefs={userColDefs}
            container={{ className: styles.usersTable }}
            rowData={orgUsers}
            enableResponsiveResize={true}
            resizeDebounceMs={150}
            tableId={`manage-users-fullscreen-${org.id}`}
          />
        ) : (
          <Stack alignItems="center" className={styles.emptyState} gap={3} justifyContent="center">
            <Text as="p" className={getTextClassName('sm')}>
              {t('no_users_in_org', { ns: 'orgs' })}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default ManageUsers;
